/**
 * Unified photo utility module for Google Places photos.
 * 
 * This module provides a single source of truth for all photo operations,
 * handling identifier-to-URL conversion, extraction, and validation.
 * 
 * Backend now sends identifiers only (name, photo_reference, author),
 * so this module focuses on working with identifiers rather than parsing URLs.
 * 
 * Features:
 * - On-demand photo fetching when photos are missing from localStorage
 * - Image validation to handle blank images in backend cache
 * - Automatic fallback to fetch fresh location data when needed
 */

import { getGooglePhotoUrl } from './googlePhotoProxy';
import { transformImageUrl } from './image.util';
import { debug, warn } from './logger';
import { getGoogleLocationDataWithAi } from '@/api/location';

/**
 * Photo object from backend (identifiers only format).
 * Backend sends this format from all endpoints (Social Scraper, AI-generated itinerary, Location Search).
 */
export interface PhotoIdentifier {
  /** New format identifier: "places/ChIJ.../photos/AZLasH..." */
  name?: string;
  /** Old format identifier: "CmRaAAAA..." */
  photo_reference?: string;
  /** Author attribution information (API may send null) */
  author?: {
    displayName?: string | null;
    uri?: string | null;
  } | null;
}

/**
 * Location data structure that may contain photos.
 * Used for extracting photos from location objects.
 */
export interface LocationDataWithPhotos {
  /** Primary photos array (backend sends identifiers here) */
  photos?: PhotoIdentifier[];
  /** Google Places data with photos (fallback source) */
  google_places_data?: {
    photos?: PhotoIdentifier[];
  };
  /** S3/HTTPS strings and/or identifier objects (photoFormat=identifiers), same as post payloads */
  image_urls?: Array<string | PhotoIdentifier | Record<string, unknown>>;
}

/**
 * Sanitize URL by removing whitespace, newlines, and control characters.
 * CSS backgroundImage and img src are very sensitive to whitespace in URLs.
 * 
 * @param url - URL string to sanitize
 * @returns Sanitized URL with all whitespace removed
 */
function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return url;
  }
  // Remove all whitespace, newlines, tabs, and control characters
  return url.trim().replace(/\s+/g, '').replace(/[\n\r\t]/g, '');
}

/**
 * Returns true if the URL is a direct Google Places/Maps photo URL.
 * We never use these for display (they can 400 when loaded via Next.js Image);
 * all place photos must be loaded via the backend proxy.
 */
export function isDirectGooglePhotoUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  return (
    url.includes('places.googleapis.com') || url.includes('maps.googleapis.com')
  );
}

/**
 * Extract single photo URL from photo identifier object.
 * Backend sends identifiers only, so this is the primary method.
 * 
 * @param photo - Photo identifier object with name or photo_reference
 * @param size - Desired photo size in pixels (default: 400)
 * @returns Proxy URL string ready for use in <img> tags, or empty string if no identifier
 * 
 * @example
 * const photo = { name: "places/ChIJ.../photos/AZLasH..." };
 * const url = getPhotoUrl(photo, 400);
 * // Returns: "https://api.globethrivers.com/api/v1/public/places/photo?name=places/ChIJ.../photos/AZLasH...&size=400"
 */
export function getPhotoUrl(
  photo: PhotoIdentifier | null | undefined,
  size: number = 400
): string {
  if (!photo) {
    debug('[photoUtils] getPhotoUrl: No photo provided');
    return '';
  }

  const url = getGooglePhotoUrl(photo, size);
  const sanitizedUrl = sanitizeUrl(url);
  
  if (url) {
    const wasSanitized = url !== sanitizedUrl;
    if (wasSanitized) {
      warn('[photoUtils] getPhotoUrl: URL was sanitized (removed whitespace)', {
        originalLength: url.length,
        sanitizedLength: sanitizedUrl.length,
      });
    }
  } else {
    debug('[photoUtils] getPhotoUrl: No identifier found in photo', {
      photoKeys: Object.keys(photo),
      photo: JSON.stringify(photo),
    });
  }

  return sanitizedUrl;
}

/**
 * Extract all photo URLs from photo array.
 * Returns array of proxy URLs ready for use in <img> tags.
 * 
 * @param photos - Array of photo identifier objects
 * @param size - Desired photo size in pixels (default: 400)
 * @returns Array of proxy URL strings, filtered to remove empty strings
 * 
 * @example
 * const photos = [
 *   { name: "places/ChIJ.../photos/AZLasH..." },
 *   { photo_reference: "CmRaAAAA..." }
 * ];
 * const urls = getPhotoUrls(photos, 400);
 * // Returns: ["https://api.globethrivers.com/api/v1/public/places/photo?name=...&size=400", ...]
 */
export function getPhotoUrls(
  photos: PhotoIdentifier[] | null | undefined,
  size: number = 400
): string[] {
  if (!photos || !Array.isArray(photos)) {
    debug('[photoUtils] getPhotoUrls: No photos array provided');
    return [];
  }

  const urls = photos
    .map((photo) => getPhotoUrl(photo, size))
    .filter((url): url is string => Boolean(url));

  return urls;
}

/**
 * Derives a canonical deduplication token from a photo identifier.
 *
 * Both new-format names ("places/ChIJ.../photos/AZLasH...") and old-format
 * photo_references ("AZLasH...") refer to the same underlying image. Extracting
 * the token after "/photos/" normalises both into the same key so that the same
 * photo is not added twice when it appears in different identifier formats across
 * multiple data sources.
 */
function photoCanonicalToken(photo: PhotoIdentifier): string | null {
  const rawId = photo.name ?? photo.photo_reference ?? '';
  if (!rawId) return null;
  const parts = rawId.split('/photos/');
  return parts.length > 1 ? (parts[1] ?? null) : rawId;
}

/**
 * If `urlStr` is our backend Google photo proxy URL, returns identifiers from its query string.
 * Used so pre-built proxy strings in `image_urls` dedupe against `photos[]` identifier objects.
 */
function parsePlacesPhotoProxyIdentifier(urlStr: string): PhotoIdentifier | null {
  if (typeof urlStr !== 'string' || !urlStr.includes('places/photo')) {
    return null;
  }
  try {
    const u = new URL(sanitizeUrl(urlStr));
    const nameParam = u.searchParams.get('name');
    if (nameParam) {
      return { name: nameParam };
    }
    const ref = u.searchParams.get('photo_reference') ?? u.searchParams.get('photoRef');
    if (ref) {
      return { photo_reference: ref };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Extract photos from location data with proper fallback chain.
 * Handles image_urls (strings + identifier objects), location.photos[], and google_places_data.photos[].
 *
 * Priority order (matches typical post/location payloads):
 * 1. locationData.image_urls[] (S3/HTTPS strings, backend proxy URL strings, and/or identifier objects)
 * 2. locationData.photos[] (Google identifiers → proxy URLs)
 * 3. locationData.google_places_data.photos[] — merged after photos[]; same-token entries are skipped
 *
 * @param locationData - Location object with photos in various formats
 * @param size - Desired photo size in pixels (default: 400)
 * @returns Array of photo URLs (proxy URLs for Google Places photos), with duplicates removed
 */
export function extractPhotosFromLocation(
  locationData: LocationDataWithPhotos | null | undefined,
  size: number = 400
): string[] {
  if (!locationData) {
    debug('[photoUtils] extractPhotosFromLocation: No locationData provided');
    return [];
  }

  const urls: string[] = [];
  // Track canonical photo tokens so the same underlying image is not added
  // twice when it appears in different identifier formats across data sources.
  const seenTokens = new Set<string>();

  const tryAddIdentifier = (photo: PhotoIdentifier): void => {
    const token = photoCanonicalToken(photo);
    if (!token || seenTokens.has(token)) return;
    seenTokens.add(token);
    const url = getPhotoUrl(photo, size);
    if (url) urls.push(url);
  };

  const tryAddImageUrlString = (raw: string): void => {
    const url = sanitizeUrl(raw);
    if (!url || isDirectGooglePhotoUrl(url)) return;

    const fromProxy = parsePlacesPhotoProxyIdentifier(url);
    if (fromProxy) {
      const token = photoCanonicalToken(fromProxy);
      if (token) {
        if (seenTokens.has(token)) return;
        seenTokens.add(token);
      }
    }
    urls.push(url);
  };

  // Priority 1: S3 / user-uploaded / backend-stored image_urls (try first; fallback to Google if these fail to load).
  // Entries may be URL strings or photo identifier objects (same as post payloads when photoFormat=identifiers).
  if (locationData.image_urls && Array.isArray(locationData.image_urls)) {
    for (const entry of locationData.image_urls) {
      if (typeof entry === 'string' && entry.length > 0) {
        tryAddImageUrlString(entry);
      } else if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        tryAddIdentifier(entry as PhotoIdentifier);
      }
    }
  }

  // Priority 2: locationData.photos[]
  if (locationData.photos && Array.isArray(locationData.photos)) {
    for (const photo of locationData.photos) {
      tryAddIdentifier(photo);
    }
  }

  // Priority 3: google_places_data.photos — merge; tryAddIdentifier skips tokens already seen
  // (covers payloads where photos mirrors google, or where extra photos exist only on google_places_data).
  if (locationData.google_places_data?.photos && Array.isArray(locationData.google_places_data.photos)) {
    for (const photo of locationData.google_places_data.photos) {
      tryAddIdentifier(photo);
    }
  }

  // Final URL-level dedup as a safety net for pre-built proxy URL strings from image_urls.
  return [...new Set(urls)];
}

/**
 * True for Instagram/TikTok CDN URLs that block hotlinking (return 403).
 * Mirrors the blocked-CDN skips in postImageExtraction/postPhotoExtraction.
 */
export function isBlockedCDNUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.includes('cdninstagram.com') ||
    lower.includes('instagram.com') ||
    lower.includes('fbcdn.net') ||
    lower.includes('tiktokcdn') ||
    lower.includes('tiktok.com')
  );
}

/**
 * Resolve a saved-countries `sample_image_urls` entry for display.
 * Handles S3/HTTPS URLs, backend Google photo proxy URLs, and identifier strings.
 * Returns '' for entries we can't display (direct Google URLs, blocked CDN hotlinks).
 */
export function resolveSampleImageDisplayUrl(
  raw: string | PhotoIdentifier | null | undefined,
  size: number = 400,
): string {
  if (!raw) return '';

  if (typeof raw === 'object') {
    return getPhotoUrl(raw, size);
  }

  const trimmed = raw.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('places/')) {
    return getPhotoUrl({ name: trimmed }, size);
  }

  if (trimmed.includes('places/photo')) {
    return sanitizeUrl(trimmed);
  }

  if (/^https?:\/\//i.test(trimmed)) {
    if (isDirectGooglePhotoUrl(trimmed) || isBlockedCDNUrl(trimmed)) return '';
    return transformImageUrl(trimmed) || trimmed;
  }

  // Old-format photo_reference (short token, not a path or URL)
  if (!trimmed.includes('/') && !trimmed.includes('.')) {
    return getPhotoUrl({ photo_reference: trimmed }, size);
  }

  return transformImageUrl(trimmed) || trimmed;
}

/**
 * Canonical dedupe key for a saved-countries `sample_image_urls` entry.
 * Aligns with `photoCanonicalToken` for Google identifiers and normalized URL for S3/HTTPS.
 */
export function sampleImageCanonicalKey(
  raw: string | PhotoIdentifier | null | undefined,
): string | null {
  if (!raw) return null;

  if (typeof raw === 'object') {
    return photoCanonicalToken(raw);
  }

  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('places/')) {
    return photoCanonicalToken({ name: trimmed });
  }

  if (trimmed.includes('places/photo')) {
    const fromProxy = parsePlacesPhotoProxyIdentifier(trimmed);
    if (fromProxy) {
      return photoCanonicalToken(fromProxy);
    }
  }

  if (/^https?:\/\//i.test(trimmed)) {
    if (isDirectGooglePhotoUrl(trimmed) || isBlockedCDNUrl(trimmed)) return null;
    return sanitizeUrl(transformImageUrl(trimmed) || trimmed);
  }

  if (!trimmed.includes('/') && !trimmed.includes('.')) {
    return trimmed;
  }

  return sanitizeUrl(transformImageUrl(trimmed) || trimmed);
}

const COLLAGE_SLOT_COUNT = 4;

/**
 * Resolve saved-countries sample entries into up to `maxCount` unique display URLs.
 * Preserves input order; skips duplicate photo identities and unrenderable entries.
 */
export function resolveUniqueSampleCollageUrls(
  raws: Array<string | PhotoIdentifier | null | undefined>,
  size: number = 400,
  maxCount: number = COLLAGE_SLOT_COUNT,
): string[] {
  const seenKeys = new Set<string>();
  const urls: string[] = [];

  for (const raw of raws) {
    if (urls.length >= maxCount) break;

    const url = resolveSampleImageDisplayUrl(raw, size);
    if (!url) continue;

    const key = sampleImageCanonicalKey(raw) ?? sanitizeUrl(url);
    if (seenKeys.has(key)) continue;

    seenKeys.add(key);
    urls.push(url);
  }

  return urls;
}

export interface SavedCollageSlot {
  src: string | undefined;
  /** Load-failure alternates only; never cross-fill empty slots with another slot's image. */
  fallbackSources: string[];
}

/**
 * Map deduped collage URLs to four grid slots without repeating images across cells.
 * Empty slots render placeholder only; assigned slots may fall back to other assigned URLs on load error.
 */
export function buildSavedCollageSlots(
  resolvedUrls: string[],
  slotCount: number = COLLAGE_SLOT_COUNT,
): SavedCollageSlot[] {
  const primaries = Array.from({ length: slotCount }, (_, index) => resolvedUrls[index] ?? '');

  return primaries.map((primary, index) => {
    if (!primary) {
      return { src: undefined, fallbackSources: [] };
    }

    const fallbackSources = primaries.filter(
      (url, slotIndex) => slotIndex !== index && Boolean(url) && url !== primary,
    );

    return { src: primary, fallbackSources };
  });
}

/**
 * Get first photo URL from location data (for single image display).
 * 
 * @param locationData - Location object with photos in various formats
 * @param size - Desired photo size in pixels (default: 400)
 * @returns First photo URL string, or empty string if no photos found
 * 
 * @example
 * const locationData = {
 *   photos: [{ name: "places/ChIJ.../photos/AZLasH..." }]
 * };
 * const url = getFirstPhotoUrl(locationData, 400);
 * // Returns: "https://api.globethrivers.com/api/v1/public/places/photo?name=...&size=400"
 */
export function getFirstPhotoUrl(
  locationData: LocationDataWithPhotos | null | undefined,
  size: number = 400
): string {
  if (!locationData) {
    debug('[photoUtils] getFirstPhotoUrl: No locationData provided');
    return '';
  }

  const urls = extractPhotosFromLocation(locationData, size);
  const firstUrl = urls[0] || '';

  return firstUrl;
}

/**
 * Validates that a photo URL actually loads (handles blank images in backend cache).
 * Uses a timeout to prevent hanging on slow/broken URLs.
 * 
 * @param photoUrl - Photo URL to validate
 * @param timeoutMs - Maximum time to wait for validation (default: 5000ms)
 * @returns Promise resolving to true if image loads successfully, false otherwise
 */
export async function validatePhotoUrl(
  photoUrl: string,
  timeoutMs: number = 5000
): Promise<boolean> {
  if (!photoUrl || typeof photoUrl !== 'string') {
    return false;
  }

  return new Promise<boolean>((resolve) => {
    const img = new window.Image();
    let timeoutId: number | null = null;
    let isResolved = false;

    const handleLoad = () => {
      if (isResolved) return;
      isResolved = true;
      cleanup();
      debug('[photoUtils] validatePhotoUrl: Image loaded successfully', { photoUrl: photoUrl.substring(0, 100) });
      resolve(true);
    };

    const handleError = () => {
      if (isResolved) return;
      isResolved = true;
      cleanup();
      warn('[photoUtils] validatePhotoUrl: Image failed to load (404 or blank)', { photoUrl: photoUrl.substring(0, 100) });
      resolve(false);
    };

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };

    img.onload = handleLoad;
    img.onerror = handleError;

    // Set timeout to prevent hanging
    timeoutId = window.setTimeout(() => {
      if (isResolved) return;
      isResolved = true;
      cleanup();
      warn('[photoUtils] validatePhotoUrl: Image validation timeout', { photoUrl: photoUrl.substring(0, 100), timeoutMs });
      resolve(false);
    }, timeoutMs);

    img.src = photoUrl;
  });
}

/**
 * Fetches fresh location data from backend when photos are missing.
 * Uses google_place_id or location name to fetch updated location data with photos.
 * 
 * @param locationData - Location data that's missing photos
 * @param cityAddress - Optional city address for context (improves AI matching)
 * @returns Promise resolving to fresh location data with photos, or null if fetch fails
 */
export async function fetchLocationPhotosOnDemand(
  locationData: LocationDataWithPhotos & { google_place_id?: string; name?: string },
  cityAddress?: string
): Promise<LocationDataWithPhotos | null> {
  // Need either google_place_id or name to fetch
  const googlePlaceId = locationData.google_place_id;
  const locationName = locationData.name;

  if (!googlePlaceId && !locationName) {
    debug('[photoUtils] fetchLocationPhotosOnDemand: No google_place_id or name available', {
      hasGooglePlaceId: !!googlePlaceId,
      hasName: !!locationName,
    });
    return null;
  }

  try {
    // Use location name for AI matching (more reliable than google_place_id alone)
    const searchTitle = locationName || googlePlaceId || '';
    
    const freshLocations = await getGoogleLocationDataWithAi({
      title: searchTitle,
      body: cityAddress || '',
    });

    if (!freshLocations || freshLocations.length === 0) {
      warn('[photoUtils] fetchLocationPhotosOnDemand: No locations returned from API', {
        searchTitle,
      });
      return null;
    }

    // Find matching location by google_place_id if available, otherwise use first result
    let matchedLocation = freshLocations[0];
    if (googlePlaceId && freshLocations.length > 1) {
      const exactMatch = freshLocations.find(
        (loc) => loc.google_place_id === googlePlaceId
      );
      if (exactMatch) {
        matchedLocation = exactMatch;
      }
    }

    // Extract photos from fresh location data
    const freshPhotos = matchedLocation.photos || [];
    const freshGooglePlacesPhotos = matchedLocation.google_places_data?.photos || [];

    // Return location data with photos
    return {
      photos: freshPhotos as PhotoIdentifier[],
      google_places_data: {
        ...matchedLocation.google_places_data,
        photos: freshGooglePlacesPhotos as PhotoIdentifier[],
      },
    };
  } catch (error) {
    warn('[photoUtils] fetchLocationPhotosOnDemand: Error fetching location data', {
      error: error instanceof Error ? error.message : String(error),
      locationName,
      googlePlaceId: googlePlaceId?.substring(0, 20),
    });
    return null;
  }
}

/**
 * Extract photos from location data with on-demand fetching and validation.
 * 
 * This is the enhanced version that:
 * 1. Tries to extract photos from existing locationData
 * 2. If no photos found AND we have google_place_id or name, fetches fresh data
 * 3. Validates that photo URLs actually load (handles blank images in cache)
 * 4. Returns only validated photo URLs
 * 
 * @param locationData - Location object with photos in various formats
 * @param size - Desired photo size in pixels (default: 400)
 * @param options - Optional configuration
 * @param options.enableOnDemandFetch - Enable on-demand fetching when photos missing (default: true)
 * @param options.validatePhotos - Validate photos actually load (default: true)
 * @param options.cityAddress - City address for context when fetching (optional)
 * @returns Promise resolving to array of validated photo URLs
 */
export async function extractPhotosFromLocationWithFallback(
  locationData: (LocationDataWithPhotos & { google_place_id?: string; name?: string }) | null | undefined,
  size: number = 400,
  options: {
    enableOnDemandFetch?: boolean;
    validatePhotos?: boolean;
    cityAddress?: string;
  } = {}
): Promise<string[]> {
  const { enableOnDemandFetch = true, validatePhotos = true, cityAddress } = options;

  // Step 1: Try extracting photos from existing locationData
  let photoUrls = extractPhotosFromLocation(locationData, size);

  // Step 2: If no photos found and on-demand fetch is enabled, try fetching fresh data
  if (photoUrls.length === 0 && enableOnDemandFetch && locationData) {
    debug('[photoUtils] extractPhotosFromLocationWithFallback: No photos found, attempting on-demand fetch', {
      hasGooglePlaceId: !!locationData.google_place_id,
      hasName: !!locationData.name,
    });

    const freshLocationData = await fetchLocationPhotosOnDemand(locationData, cityAddress);
    
    if (freshLocationData) {
      // Extract photos from fresh data
      photoUrls = extractPhotosFromLocation(freshLocationData, size);
      
      if (photoUrls.length > 0) {
        debug('[photoUtils] extractPhotosFromLocationWithFallback: Successfully fetched photos on-demand', {
          photosCount: photoUrls.length,
        });
      }
    }
  }

  // Step 3: Validate photos if enabled (handles blank images in backend cache)
  if (validatePhotos && photoUrls.length > 0) {
    debug('[photoUtils] extractPhotosFromLocationWithFallback: Validating photo URLs', {
      urlsCount: photoUrls.length,
    });

    const validationResults = await Promise.all(
      photoUrls.map((url) => validatePhotoUrl(url, 5000))
    );

    // Filter to only validated URLs
    const validatedUrls = photoUrls.filter((_, index) => validationResults[index]);
    
    if (validatedUrls.length < photoUrls.length) {
      warn('[photoUtils] extractPhotosFromLocationWithFallback: Some photos failed validation', {
        total: photoUrls.length,
        validated: validatedUrls.length,
        failed: photoUrls.length - validatedUrls.length,
      });
    }

    return validatedUrls;
  }

  return photoUrls;
}

/**
 * Get first photo URL with on-demand fetching and validation.
 * Enhanced version of getFirstPhotoUrl that handles missing photos and blank cache images.
 * 
 * @param locationData - Location object with photos in various formats
 * @param size - Desired photo size in pixels (default: 400)
 * @param options - Optional configuration (same as extractPhotosFromLocationWithFallback)
 * @returns Promise resolving to first validated photo URL, or empty string if none found
 */
export async function getFirstPhotoUrlWithFallback(
  locationData: (LocationDataWithPhotos & { google_place_id?: string; name?: string }) | null | undefined,
  size: number = 400,
  options: {
    enableOnDemandFetch?: boolean;
    validatePhotos?: boolean;
    cityAddress?: string;
  } = {}
): Promise<string> {
  const urls = await extractPhotosFromLocationWithFallback(locationData, size, options);
  const firstUrl = urls[0] || '';
  
  if (firstUrl) {
    debug('[photoUtils] getFirstPhotoUrlWithFallback: Found first validated photo', { size });
  } else {
    debug('[photoUtils] getFirstPhotoUrlWithFallback: No validated photos found');
  }

  return firstUrl;
}
